import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect, useRef, lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import { trackPixel } from "@/lib/pixel";

// Fires PageView on SPA route changes. The base code in index.html covers the initial
// load, so we seed the guard with the landing path; re-runs for the same path are no-ops
// (safe under React StrictMode double-mounting).
let pixelLastPath = typeof window !== "undefined" ? window.location.pathname + window.location.search : "";
function PixelPageView() {
  const location = useLocation();
  useEffect(() => {
    const current = location.pathname + location.search;
    if (current === pixelLastPath) return;
    pixelLastPath = current;
    trackPixel("PageView");
  }, [location.pathname, location.search]);
  return null;
}
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { WishlistProvider } from "@/contexts/WishlistContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import InstallBanner from "@/components/InstallBanner";
import NewsletterPopup from "@/components/NewsletterPopup";
import WhatsAppButton from "@/components/WhatsAppButton";
import { OrganizationJsonLd } from "@/components/SEOHead";
import { Loader2 } from "lucide-react";

// Critical path — loaded immediately
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import Bundles from "@/pages/Bundles";
import BundleDetail from "@/pages/BundleDetail";
import Sale from "@/pages/Sale";

// Lazy loaded — reduces initial bundle by ~60%
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Cart = lazy(() => import("@/pages/Cart"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Profile = lazy(() => import("@/pages/Profile"));
const Contact = lazy(() => import("@/pages/Contact"));
const BulkOrders = lazy(() => import("@/pages/BulkOrders"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const Ambassador = lazy(() => import("@/pages/Ambassador"));
const Affiliate = lazy(() => import("@/pages/Affiliate"));
const Policies = lazy(() => import("@/pages/Policies"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminPayouts = lazy(() => import("@/pages/AdminPayouts"));
const AdminPartners = lazy(() => import("@/pages/AdminPartners"));
const AffiliateDashboard = lazy(() => import("@/pages/AffiliateDashboard"));
const AmbassadorDashboard = lazy(() => import("@/pages/AmbassadorDashboard"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Blog = lazy(() => import("@/pages/Blog"));
const Competition = lazy(() => import("@/pages/Competition"));
const CompetitionRules = lazy(() => import("@/pages/CompetitionRules"));
const CompetitionConfirm = lazy(() => import("@/pages/CompetitionConfirm"));
const RugbyShortsFAQ = lazy(() => import("@/pages/RugbyShortsFAQ"));
const Fans = lazy(() => import("@/pages/Fans"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const CheckoutResult = lazy(() => import("@/pages/CheckoutResult").then(m => ({ default: m.CheckoutSuccess })));
const CheckoutCancelled = lazy(() => import("@/pages/CheckoutResult").then(m => ({ default: m.CheckoutCancelled })));
const CheckoutFailed = lazy(() => import("@/pages/CheckoutResult").then(m => ({ default: m.CheckoutFailed })));

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-24">
      <Loader2 size={32} className="animate-spin text-[#F7931E]" />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
          <OrganizationJsonLd />
          <PixelPageView />
          <div className="min-h-screen bg-[#0A0A0A] flex flex-col">
            <Navbar />
            <main className="flex-1">
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/shop/:category" element={<Shop />} />
                  <Route path="/deals" element={<Bundles />} />
                  <Route path="/bundles" element={<Bundles />} />
                  <Route path="/bundles/:slug" element={<BundleDetail />} />
                  <Route path="/sale" element={<Sale />} />
                  <Route path="/product/:id" element={<ProductDetail />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/bulk-orders" element={<BulkOrders />} />
                  <Route path="/gallery" element={<Gallery />} />
                  <Route path="/ambassador" element={<Ambassador />} />
                  <Route path="/ambassador-dashboard" element={<AmbassadorDashboard />} />
                  <Route path="/affiliate" element={<Affiliate />} />
                  <Route path="/affiliate-dashboard" element={<AffiliateDashboard />} />
                  <Route path="/policies" element={<Policies />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<Blog />} />
                  <Route path="/rugby-shorts-guide" element={<RugbyShortsFAQ />} />
                  <Route path="/competition" element={<Competition />} />
                  <Route path="/competition/rules" element={<CompetitionRules />} />
                  <Route path="/competition/confirm" element={<CompetitionConfirm />} />
                  <Route path="/fans" element={<Fans />} />
                  <Route path="/unsubscribe" element={<Unsubscribe />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/checkout/success" element={<CheckoutResult />} />
                  <Route path="/checkout/cancelled" element={<CheckoutCancelled />} />
                  <Route path="/checkout/failed" element={<CheckoutFailed />} />
                  <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                  <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
                  <Route path="/admin/payouts" element={<ProtectedRoute adminOnly><AdminPayouts /></ProtectedRoute>} />
                  <Route path="/admin/partners" element={<ProtectedRoute adminOnly><AdminPartners /></ProtectedRoute>} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </main>
            <Footer />
          </div>
          <InstallBanner />
          <NewsletterPopup />
          <WhatsAppButton />
          <Toaster position="top-right" theme="dark" richColors />
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
